/**
 * Rappresentazione del denaro in Qota.
 *
 * INVARIANTE FONDAMENTALE: gli importi sono **interi di centesimi**, mai
 * decimali. `0.1 + 0.2 !== 0.3` in virgola mobile, e su un'app che divide conti
 * fra amici un centesimo perso è un bug che qualcuno nota davvero.
 *
 * A database la colonna è `bigint`; in JavaScript la trattiamo come `number`.
 * È sicuro: `Number.MAX_SAFE_INTEGER` centesimi valgono più di 90.000 miliardi
 * di euro. Usiamo `bigint` sulla colonna, non `integer`, per non dover migrare
 * il giorno in cui qualcuno registra una spesa in una valuta a inflazione alta.
 */

/** Valute supportate. L'elenco cresce; il codice non deve assumerne una. */
export const CURRENCIES = ["EUR", "USD", "GBP", "CHF"] as const;
export type Currency = (typeof CURRENCIES)[number];

export interface Money {
  /** Importo in centesimi. Sempre intero, può essere negativo (un debito). */
  readonly cents: number;
  readonly currency: Currency;
}

export class MoneyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MoneyError";
  }
}

/** Costruisce un importo, rifiutando tutto ciò che non è un intero sicuro. */
export function money(cents: number, currency: Currency): Money {
  if (!Number.isInteger(cents)) {
    throw new MoneyError(`Gli importi sono centesimi interi, ricevuto ${cents}`);
  }
  if (!Number.isSafeInteger(cents)) {
    throw new MoneyError(`Importo fuori dall'intervallo sicuro: ${cents}`);
  }
  return { cents, currency };
}

export function zero(currency: Currency): Money {
  return { cents: 0, currency };
}

export function isZero(value: Money): boolean {
  return value.cents === 0;
}

export function isNegative(value: Money): boolean {
  return value.cents < 0;
}

/**
 * Sommare euro e dollari non ha significato: è un errore di programmazione,
 * non un caso da gestire a runtime.
 */
function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new MoneyError(`Valute incompatibili: ${a.currency} e ${b.currency}`);
  }
}

export function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.cents + b.cents, a.currency);
}

export function subtract(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.cents - b.cents, a.currency);
}

export function negate(value: Money): Money {
  return money(-value.cents, value.currency);
}

export function sum(values: readonly Money[], currency: Currency): Money {
  return values.reduce<Money>((total, value) => add(total, value), zero(currency));
}

/** `-1` se a < b, `0` se uguali, `1` se a > b. */
export function compare(a: Money, b: Money): number {
  assertSameCurrency(a, b);
  return Math.sign(a.cents - b.cents);
}

export function equals(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.cents === b.cents;
}

/**
 * Divide un importo in `parts` quote il più uguali possibile.
 *
 * Il resto viene distribuito un centesimo alla volta a partire dalla prima
 * quota: 10,00 € fra 3 diventa 3,34 / 3,33 / 3,33 — mai 3,33 / 3,33 / 3,33 con
 * un centesimo svanito nel nulla.
 *
 * GARANZIA: la somma del risultato è **esattamente** uguale al totale.
 */
export function splitEvenly(total: Money, parts: number): Money[] {
  if (!Number.isInteger(parts) || parts <= 0) {
    throw new MoneyError(`Il numero di quote dev'essere un intero positivo, ricevuto ${parts}`);
  }

  const sign = total.cents < 0 ? -1 : 1;
  const absolute = Math.abs(total.cents);

  const base = Math.floor(absolute / parts);
  const remainder = absolute - base * parts;

  return Array.from({ length: parts }, (_, index) =>
    money(sign * (base + (index < remainder ? 1 : 0)), total.currency),
  );
}

/**
 * Ripartisce un importo secondo pesi arbitrari — quote, percentuali, unità.
 *
 * Usa il metodo del **resto maggiore**: si assegna a ciascuno la parte intera,
 * poi i centesimi avanzati vanno a chi ha la frazione più grande. È il criterio
 * che minimizza lo scarto rispetto alla ripartizione esatta, ed è quello che
 * l'utente si aspetta guardando i numeri.
 *
 * GARANZIA: la somma del risultato è **esattamente** uguale al totale.
 */
export function allocate(total: Money, weights: readonly number[]): Money[] {
  if (weights.length === 0) {
    throw new MoneyError("Serve almeno un peso per ripartire un importo");
  }
  if (weights.some((weight) => !Number.isFinite(weight) || weight < 0)) {
    throw new MoneyError("I pesi devono essere numeri finiti non negativi");
  }

  const totalWeight = weights.reduce((acc, weight) => acc + weight, 0);
  if (totalWeight <= 0) {
    throw new MoneyError("La somma dei pesi dev'essere maggiore di zero");
  }

  const sign = total.cents < 0 ? -1 : 1;
  const absolute = Math.abs(total.cents);

  const exact = weights.map((weight) => (absolute * weight) / totalWeight);
  const floors = exact.map((value) => Math.floor(value));
  const distributed = floors.reduce((acc, value) => acc + value, 0);

  // I centesimi avanzati vanno a chi ha la frazione più alta; a parità di
  // frazione vince l'indice più basso, così il risultato è deterministico.
  const leftovers = absolute - distributed;
  const order = exact
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  const result = [...floors];
  for (let i = 0; i < leftovers; i++) {
    const target = order[i % order.length];
    if (target) result[target.index] = (result[target.index] ?? 0) + 1;
  }

  return result.map((cents) => money(sign * cents, total.currency));
}

const DECIMAL_PLACES: Record<Currency, number> = {
  EUR: 2,
  USD: 2,
  GBP: 2,
  CHF: 2,
};

/** Formatta per la visualizzazione. Solo presentazione: mai per i calcoli. */
export function formatMoney(value: Money, locale = "it-IT"): string {
  const places = DECIMAL_PLACES[value.currency];
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: value.currency,
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  }).format(value.cents / 10 ** places);
}

/**
 * Converte l'input dell'utente ("12,50", "12.5", "12") in centesimi.
 * Restituisce `null` se la stringa non è un importo valido: la validazione
 * appartiene al chiamante, non a un'eccezione.
 */
export function parseAmount(input: string, currency: Currency): Money | null {
  const normalized = input.trim().replace(/\s/g, "").replace(",", ".");
  if (!/^-?\d+(\.\d+)?$/.test(normalized)) return null;

  const places = DECIMAL_PLACES[currency];
  const [wholePart = "0", fractionPart = ""] = normalized.split(".");

  // Tronchiamo invece di arrotondare: "12.999" non deve diventare 13,00 €.
  const fraction = fractionPart.padEnd(places, "0").slice(0, places);
  const negative = wholePart.startsWith("-");
  const magnitude = Number(wholePart.replace("-", "") + fraction);

  if (!Number.isSafeInteger(magnitude)) return null;
  return money(negative ? -magnitude : magnitude, currency);
}
